/**
 * FloatingHomeButton Component
 * 
 * Playful floating button that appears when scrolling on long static pages.
 * Provides quick navigation back to homepage with ORO's authentic voice.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegisterFab } from '../contexts/PWAContext';

interface FloatingHomeButtonProps {
  threshold?: number;
  label?: string;
  className?: string;
}

export default function FloatingHomeButton({
  threshold = 250,
  label = "Take me home",
  className = ""
}: FloatingHomeButtonProps) {
  const [isVisible, setIsVisible] = useState(() => {
    // Initialize with current scroll position if available
    if (typeof window !== 'undefined') {
      return window.scrollY > threshold;
    }
    return false;
  });
  const navigate = useNavigate();

  // Suppress the PWA install prompt while this FAB is on screen (#3096).
  useRegisterFab(isVisible);

  // Throttled scroll handler for performance
  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    setIsVisible(scrollY > threshold);
  }, [threshold]);

  useEffect(() => {
    // Add throttling for performance
    let ticking = false;
    
    const throttledScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    
    return () => window.removeEventListener('scroll', throttledScrollHandler);
  }, [handleScroll]);

  const handleClick = () => {
    navigate('/');
  };

  if (!isVisible) {
    return null;
  }

  return (
    <button
      onClick={handleClick}
      aria-label="Return to the home page"
      data-testid="floating-home-button"
      className={`
        fixed bottom-24 right-6 z-40
        bg-accent text-black
        px-4 py-3 rounded-full
        shadow-lg hover:shadow-xl
        transition-all duration-300 ease-out
        opacity-80 hover:opacity-100
        hover:scale-105
        focus:outline-none focus:ring-2 focus:ring-accent focus:ring-opacity-50
        font-medium text-sm
        min-h-11 min-w-11
        animate-in fade-in slide-in-from-bottom-4
        ${className}
      `}
    >
      {label}
    </button>
  );
}